import React, { useMemo, useState } from "react";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import TableCell from "@mui/material/TableCell";
import FMTable from "../../../FMTable";
import {
    AccordionDetailsM,
    AccordionM,
    AccordionSummaryM,
    AccordionTable,
    CancelButton,
    ConfirmButton,
    Container,
    MainTable,
    SpaceSettler,
    StyledGrid,
    TitleTypography,
} from "./Styled";
import FMReadingCard from '../../../FMCard'
import FMChart from "../../../FMChart";
import { useSelector } from "react-redux";
import { isArray } from "lodash";


const FMSectionsFormComponent = ({
    conversation,
    fields,
    options,
    collectionData,
    dashboardsData,
    handleConfirm,
    handleCancel,
    renderFields,
    disableSubmit,
    parentTabId,
    parentDocId,
    cardsSetData,
    onCardAction,
    handleCheckSubmitButton,
}) => {

    const isOpenChatWidget = useSelector((state) => state.bot.isOpenChatWidget);

    const [expanded, setExpanded] = useState(() => {
        if (!options?.sections) return [];

        return Object.entries(options.sections)
            .filter(([_, section]) => !section.collapsable || !section.defaultCollapsableState)
            .map(([sectionId]) => sectionId);
    });

    const sectionsAvailable = Object.keys(options?.sections || {});

    const processedFields = useMemo(() => {
        if (!Array.isArray(fields)) return [];

        return fields.filter((field) => {
            const section = field.section;

            if (!section || !sectionsAvailable.includes(section)) return false;
            if (field.column == null || !Number.isInteger(field.column)) return false;

            const sectionConfig = options.sections[section];
            const maxColumns = sectionConfig?.columns;

            // If `columns` is undefined, include the field
            if (!Number.isInteger(maxColumns)) return true;

            // Otherwise, check if column index is within range
            return field.column >= 0 || field.column >= 1 && field.column < maxColumns;
        });
    }, [fields, options, sectionsAvailable]);

    const cardSetData = (parsedHtml, isLayoutRow, sectionLength, section) => {
        if (parsedHtml) {
            // Ensure parsedHtml is an array and extract the cards and options properly
            let cardsList = Array.isArray(parsedHtml) && parsedHtml.length > 0 ? parsedHtml[0].cards : parsedHtml.cards || [];
            let cardDetail = Array.isArray(parsedHtml) && parsedHtml.length > 0 ? parsedHtml[0].options : parsedHtml.options || null;

            return (
                <FMReadingCard
                    data={cardsList}
                    itemParent={cardDetail}
                    onCardAction={onCardAction}
                    isLayoutRow={isLayoutRow}
                    sectionLength={sectionLength}
                    sections={section}
                />
            );
        }

        // Handle case where parsedHtml is not provided or is invalid
        console.log("No valid parsedHtml data found");
        return null;
    };

    const dashboardData = (data, section) => {
        if (!data && !section) return;

        return (
            <FMChart message={data} section={section} />
        );
    };

    //section?.readOnly  we are getting for new ui sections
    const renderSectionContent = (fields, section, options, isLayoutRow, sectionLength) => {
        const getGridClasses = (columns5) => {
            if (columns5) {
                if (isOpenChatWidget && !isLayoutRow) {
                    return "grid grid-cols-1 gap-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3";
                }

                return "grid grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 relative";
            }
            else {
                if (isOpenChatWidget && !isLayoutRow) {
                    return "grid grid-cols-1 gap-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3";
                }

                if (isLayoutRow && sectionLength > 1) {
                    return "grid grid-cols-1 gap-1";
                }

                return "grid grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 relative";
            }
        };

        const getFieldWrapperClass = (field) => {
            if (field.type === "buttons_field") {
                if (field?.options?.length === 1) return "under-btn";
                if (field?.options?.length === 2) return "relative-bts";
            }
            return "";
        };

        return (
            <>
                {
                    section?.columns === 5 ?
                        <div className={getGridClasses(true)}>
                            {fields?.map((field) => {
                                const comp = field && renderFields(field, true, section);
                                return (
                                    <div key={field.id} className={getFieldWrapperClass(field)}>
                                        {comp}
                                    </div>
                                );
                            })}
                        </div>
                        :
                        section?.readOnly ?
                            <div className={getGridClasses()}>
                                {fields?.map((field) => {
                                    const comp = field && renderFields(field, true, section);
                                    return (
                                        <div key={field.id} className={getFieldWrapperClass(field)}>
                                            {comp}
                                        </div>
                                    );
                                })}
                            </div>
                            :
                            <div className="w-full">
                                <table className="w-full">
                                    <TableBody sx={{ marginTop: "10px" }} className="!w-full">
                                        {section.columns === 2 ? (
                                            fields?.map(([field1, field2]) => {
                                                const comp1 = field1 && renderFields(field1, true, section);
                                                const comp2 = field2 && renderFields(field2, true, section);

                                                return (
                                                    <TableRow key={field1.id} className="!flex under-tabel gap-7 items-center">
                                                        {comp1}
                                                        {field2 ? comp2 : null}
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <>
                                                {fields.map((f) => (
                                                    <React.Fragment key={f.id}>
                                                        <TableRow>
                                                            {renderFields(f, true, section)}
                                                        </TableRow>
                                                    </React.Fragment>
                                                ))}
                                            </>
                                        )}
                                    </TableBody>
                                </table>
                            </div>
                }
            </>
        );
    }

    const renderCollectionTable = ({ options: childOptions = {}, rows = [] } = {}) => {
        return <FMTable
            conversation={conversation}
            options={childOptions}
            message={rows}
            {...childOptions}
            parentTabId={parentTabId}
            parentDocId={parentDocId}
            mainParentDocId={options?.docId}
            setDisableSubmit={handleCheckSubmitButton}
            disableSubmit={disableSubmit}
            parentOptions={options}
        />;
    };

    const pairFields = (fields) => {
        const blank_field = { type: "blank" };
        const no_field = { type: "no_field" };

        let paired = [];
        let buffer = [];

        for (let i = 0; i < fields.length; i++) {
            if (fields[i].column === 0) {
                if (buffer[0]) {
                    buffer[1] = ["file_field"].includes(buffer[0].type) ? no_field : blank_field;
                    paired.push([...buffer]);
                    buffer = [];
                }
                buffer[0] = fields[i];
            } else if (fields[i].column === 1) {
                if (fields[i].value === "" && fields[i].section === "userRegistrationSection") {
                    delete fields[i].value;
                }
                if (buffer[0]) {
                    if (["file_field"].includes(buffer[0].type)) {
                        buffer[1] = no_field;
                        paired = [...paired, [...buffer]];
                        buffer = [];
                        buffer[0] = blank_field;
                        buffer[1] = fields[i];
                        paired = [...paired, [...buffer]];
                        buffer = [];
                    } else {
                        buffer[1] = fields[i];
                        paired = [...paired, [...buffer]];
                        buffer = [];
                    }
                } else {
                    buffer[0] = blank_field;
                    buffer[1] = fields[i];
                    paired = [...paired, [...buffer]];
                    buffer = [];
                }
            }
        }

        if (buffer[0] && !buffer[1]) {
            buffer[1] = ["file_field"].includes(buffer[0].type) ? no_field : blank_field;
            paired = [...paired, [...buffer]];
        } else if (!buffer[0] && buffer[1]) {
            buffer[0] = blank_field;
            paired = [...paired, [...buffer]];
        }

        return paired;
    };

    const renderedSections = () => {
        if (!options?.sections) return null;

        const allSections = Object.entries(options.sections).filter(([_, section]) => !section.hidden);

        // Group sections by row
        const sectionsByRow = allSections.reduce((acc, [sectionId, section]) => {
            const row = section.grid?.row ?? 0;
            if (!acc[row]) acc[row] = [];
            acc[row].push([sectionId, section]);
            return acc;
        }, {});

        // Sort sections within each row by column
        Object.values(sectionsByRow).forEach(rowSections => {
            rowSections.sort((a, b) => (a[1].grid?.column ?? 0) - (b[1].grid?.column ?? 0));
        });

        const renderSection = (sectionId, section, index, isLayoutRow, sectionLength, hasNewUISection) => {
            let processedFieldsBySectionId = processedFields.filter((fld) => sectionId === fld.section);

            if (section.columns === 2 && !section.readOnly) {
                processedFieldsBySectionId = pairFields(processedFieldsBySectionId);
            }
            const isExpanded = !section.collapsable || expanded.includes(sectionId);
            return (
                <AccordionM
                    id={`accordion-${sectionId || ''}`}
                    // className="!p-0 h-full"
                    className={section?.borderless ? "!p-0 !bg-transparent" : " h-full !py-0 !pb-2 !pt-2 !px-3 shadow-shd-on "}
                    hasNewUISection={hasNewUISection}
                    expanded={isExpanded}
                    onChange={(_, isExpanded) => {
                        if (section.collapsable) {
                            setExpanded((prev) =>
                                isExpanded ? [...prev, sectionId] : prev.filter((id) => id !== sectionId)
                            );
                        }
                    }}
                >

                    {section.label && section.collapsable && !section.readOnly ? (
                        <AccordionSummaryM
                            expandIcon={null}
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: section.collapsable ? "pointer" : "default",
                                pointerEvents: section.collapsable ? "auto" : "none",
                                padding: 0,
                            }}
                        >
                            <div className="bg-[#F6FBFF] w-full py-4 px-6 text-size-14 font-medium text-[#2C2F44] rounded-[15px_15px_0_0] mb-5 flex items-center justify-between">
                                {section.label}

                                {section.collapsable && (
                                    <div className=" ">
                                        {isExpanded ? (
                                            // Expanded (Up Arrow)
                                            <svg width="24" height="24" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect width="30" height="30" rx="15" fill="#15194B" />
                                                <rect x="0.5" y="0.5" width="29" height="29" rx="14.5" stroke="black" strokeOpacity="0.1" />
                                                <path d="M9 18.5L15.5 12L22 18.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        ) : (
                                            // Collapsed (Down Arrow)
                                            <svg width="24" height="24" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect width="30" height="30" rx="15" fill="#15194B" />
                                                <rect x="0.5" y="0.5" width="29" height="29" rx="14.5" stroke="black" strokeOpacity="0.1" />
                                                <path d="M9 12L15.5 18.5L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        )}
                                    </div>
                                )}
                            </div>
                        </AccordionSummaryM>
                    ) : null}

                    <AccordionDetailsM className={`${isArray(fields) && fields.length === 0 ? '!p-2' : "!pt-0 !pb-6"} !flex flex-col items-center justify-center`}>
                        {/* {!section.label && <SpaceSettler iscollection={section.forCollection} />} */}
                        <AccordionTable className={section.forCollection ? "" : "overflow-remove pb-0 pt-0"}>
                            <MainTable aria-label="customized table">
                                <tbody>
                                    <tr>
                                        <td>
                                            <div
                                                className={sectionLength > 1 && cardsSetData?.[sectionId]
                                                    ? "w-full height-outer px-3 flex items-center justify-center"
                                                    : sectionLength > 1
                                                        ? "w-full height-outer"
                                                        : ""}
                                            >
                                                {
                                                    section.forCollection
                                                        ? renderCollectionTable(collectionData?.[sectionId])
                                                        : cardsSetData?.[sectionId]
                                                            ? cardSetData(cardsSetData[sectionId], isLayoutRow, sectionLength, section)
                                                            : dashboardsData?.[sectionId]
                                                                ? dashboardData(dashboardsData[sectionId], section)
                                                                : renderSectionContent(processedFieldsBySectionId, section, options, isLayoutRow, sectionLength)
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </MainTable>
                        </AccordionTable>
                    </AccordionDetailsM>
                </AccordionM>
            );
        };

        return (
            <>
                {Object.entries(sectionsByRow).map(([row, rowSections]) => {
                    const isLayoutRow = rowSections.length > 1;

                    //Check if the row contains any new UI (grid) section
                    const hasNewUISection = rowSections.some(([_, section]) => section.grid);

                    const containerClass = isLayoutRow
                        ? "flex flex-row gap-4 w-full items-stretch under-cards mb-4"
                        : "flex flex-col w-full items-stretch under-cards";

                    return (
                        <div key={row} className={hasNewUISection ? containerClass : {}}>
                            {rowSections.map(([sectionId, section], idx) => (
                                <div
                                    key={sectionId}
                                    className={isLayoutRow ? "flex-1" : ""}
                                    style={isLayoutRow && hasNewUISection ? { width: `${100 / rowSections.length}%` } : undefined}
                                >
                                    {renderSection(sectionId, section, idx, isLayoutRow, rowSections.length, hasNewUISection)}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </>
        );
    };

    // TODO: Disable main save button while any row is being edited or saved
    const hasTrueRowOption = () => {
        // Get values of collectionData object or empty array if undefined/null
        const rowData = collectionData ? Object.values(collectionData) : [];
        // Defensive: check if rowData exists and has at least one element with .rows array
        if (rowData.length > 0 && Array.isArray(rowData[0].rows)) {
            // Iterate over each row
            for (const row of rowData[0].rows) {
                // Iterate over each key in the row object
                for (const key in row) {
                    const child = row[key];
                    // Check if child has rowOptions and editRow is true
                    if (child?.rowOptions?.editRow === true) {
                        return true; // Found an editing row
                    }
                }
            }
        }

        return false;
    };

    const isSaveDisabled = hasTrueRowOption();

    return (
        <>
            <Container style={{ width: '100%', minWidth: "100%" }}>{renderedSections()}</Container>
            {(options?.confirm || options?.cancel) && (
                <TableContainer>
                    <MainTable aria-label="customized table">
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <StyledGrid
                                        container
                                        spacing={2}
                                        direction="row"
                                        justifyContent="left"
                                        alignItems="center"
                                    >
                                        {options?.confirm && (
                                            <Grid>
                                                <ConfirmButton
                                                    className="confirm-button"
                                                    variant="contained"
                                                    disabled={isSaveDisabled || disableSubmit}
                                                    onClick={() => handleConfirm()}
                                                >
                                                    {options.confirm}
                                                </ConfirmButton>
                                            </Grid>
                                        )}
                                        {options?.cancel && (
                                            <Grid>
                                                <CancelButton
                                                    className="cancel-button"
                                                    variant="outlined"
                                                    onClick={() => handleCancel()}
                                                >
                                                    {options.cancel}
                                                </CancelButton>
                                            </Grid>
                                        )}
                                    </StyledGrid>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </MainTable>
                </TableContainer>
            )}
        </>
    );
}

export default FMSectionsFormComponent;